import React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { FiBox, FiInfo } from "react-icons/fi";
import {
  getUserModel3DTitle,
  getUserModel3DUploaderLabel,
  UserModel3DLibraryItem,
} from "../../../features/floorplan3d/userModels/userModel3DTypes";
import { resolveUserModel3DUrl } from "../../../features/floorplan3d/userModels/userModel3DUrls";
import { floorplannerStore } from "../../../store/floorplannerStore";
import { useCurrencyRates } from "../../../hooks/useCurrencyRates";
import { formatAmountInDisplayCurrency } from "../../../utils/currency";
import { CommunityFavoriteButton } from "./CommunityFavoriteButton";

type Props = {
  model: UserModel3DLibraryItem;
  onInsert: (model: UserModel3DLibraryItem) => void;
  onMoreInfo: (model: UserModel3DLibraryItem) => void;
};

/**
 * Community library card: product photo by default, 3D thumbnail on hover.
 * Falls back to the 3D thumbnail when no source image is available.
 * The 3D thumbnail is only requested after hover/focus so a page of cards
 * does not fetch two images each.
 * Brand / price mirror catalog cards when commerce fields are set.
 */
export const CommunityModelCard: React.FC<Props> = observer(({
  model,
  onInsert,
  onMoreInfo,
}) => {
  const { t, i18n } = useTranslation();
  const { ratesArray, base } = useCurrencyRates();
  const [hovered, setHovered] = React.useState(false);
  const productImage = resolveUserModel3DUrl(model.sourceImageUrl);
  const thumbnail = resolveUserModel3DUrl(model.thumbnailUrl);
  const displayUrl =
    hovered && thumbnail ? thumbnail : productImage || thumbnail;
  const modelTitle = getUserModel3DTitle(model);
  const uploaderLabel = getUserModel3DUploaderLabel(model);
  const brand = model.brand?.trim();
  const eyebrow = brand || uploaderLabel;
  const formattedPrice = formatAmountInDisplayCurrency(
    model.price,
    model.currency || "SEK",
    floorplannerStore.currency,
    ratesArray,
    base,
    i18n.language,
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onInsert(model)}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onInsert(model);
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      className="group relative flex w-full cursor-pointer flex-col overflow-hidden rounded-[10px] bg-[#F7F6F4] text-left shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]"
    >
      <div className="relative flex h-44 w-full items-center justify-center overflow-hidden bg-white sm:h-48">
        {displayUrl ? (
          <img
            src={displayUrl}
            alt=""
            loading="lazy"
            decoding="async"
            crossOrigin="use-credentials"
            className="h-full w-full object-contain p-4"
          />
        ) : (
          <FiBox size={34} className="text-gray-400" />
        )}
        <CommunityFavoriteButton model={model} />
      </div>
      <div className="flex min-h-[72px] flex-1 flex-col gap-1 bg-[#F7F6F4] p-2.5">
        <p className="truncate text-[10px] font-medium uppercase tracking-widest text-[#5a5752] sm:text-[11px]">
          {eyebrow}
        </p>
        <p
          className="truncate text-[13px] font-medium leading-tight text-[#111] sm:text-[14px]"
          title={modelTitle}
        >
          {modelTitle}
        </p>
        {brand ? (
          <p className="truncate text-[10px] text-[#777] sm:text-[11px]">
            {uploaderLabel}
          </p>
        ) : null}
        <div className="mt-auto flex items-end justify-between gap-2">
          {formattedPrice ? (
            <span className="min-w-0 text-[15px] font-semibold text-[#0D0C0C] sm:text-[16px]">
              {formattedPrice}{" "}
              <span className="text-[10px] font-medium sm:text-[11px]">
                {t("common.inclVat")}
              </span>
            </span>
          ) : (
            <p className="line-clamp-1 min-w-0 text-[10px] text-[#777] sm:text-[11px]">
              {model.displayDescription || model.originalFilename}
            </p>
          )}
          <button
            type="button"
            aria-label={`Details for ${modelTitle}`}
            onClick={(event) => {
              event.stopPropagation();
              onMoreInfo(model);
            }}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#777] hover:bg-[#E5E3DE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]"
          >
            <FiInfo size={14} />
          </button>
        </div>
      </div>
    </div>
  );
});
